import { graphql } from 'react-apollo';
import { OptionProps } from '@apollo/react-hoc/lib/types';
import { toast } from 'react-toastify';
import {
  FAILED_CANCEL_SUBSCRIPTION_MSG,
  FAILED_CHANGE_FREE_TRIAL_CLINIC_SUBSCRIPTION,
  FAILED_CREATE_SUBSCRIPTION_MSG,
  FAILURE_EDIT_PATIENT,
  SUCCESSFULLY_CANCEL_SUBSCRIPTION_MSG,
  SUCCESSFULLY_CREATED_CLINIC_SUBSCRIPTION_PLAN_MSG,
  SUCCESSFULLY_EDIT_PATIENT,
  SUCCESSFULLY_SET_CLINIC_FREE_TRIAL_SUBSCRIPTION_PLAN_MSG,
  SUCCESSFULLY_STOP_CLINIC_FREE_TRIAL_SUBSCRIPTION_PLAN_MSG,
} from '../localization';
import logError from '../../utils/logError';
import { messageFromError } from '../ui';
import {
  CANCEL_SUBSCRIPTIONS,
  CANCEL_SUBSCRIPTIONS_FOR_CLINIC,
  MAKE_SUBSCRIPTION_FOR_CLINIC,
  SET_FREE_TRIAL_SUBSCRIPTION_FOR_CLINIC,
  USER_ACCESS,
} from './payment';
import { ClinicAdmin, Fee, PatientType, UserType } from '../types';
import { MEDICINES_PROCEDURES_QUERY } from './medicineProcedure';
import pathOr from 'ramda/es/pathOr';
import { DOCTORS_FOR_CLINIC, UPDATE_PATIENT_BY_CLINIC_MUTATION } from './gql';
import { IFormEditPatientByClinicValues } from '../../pages/PatientsForClinic/EditPatientByClinic/EditPatientByClinicForm';
import { setProceduresInput } from '../../utils/setProceduresInput';
import { setMedicinesInput } from '../../utils/setMedicinesInput';
import { UserAccess } from 'menhealth-subscription-validation';
import { GET_CLINIC_BY_SMARTLINK, GET_DOCTOR_BY_SMARTLINK } from './smartlink';

export const cancelSubscriptionMutation = graphql(CANCEL_SUBSCRIPTIONS, {
  options: {
    refetchQueries: ['activeUserSubscriptions', 'me'],
  },
  props: ({ mutate }: OptionProps<any>) => ({
    cancelSubscriptions: () =>
      mutate({})
        .then((_) => {
          toast(SUCCESSFULLY_CANCEL_SUBSCRIPTION_MSG, { type: 'info' });
        })
        .catch((error: Error) => {
          logError('FAILED CANCEL SUBSCRIPTION', error);
          toast(
            `${FAILED_CANCEL_SUBSCRIPTION_MSG}: ${messageFromError(error)}`,
            { type: 'error' },
          );
        }),
  }),
});

export const cancelSubscriptionForClinicMutation = graphql(
  CANCEL_SUBSCRIPTIONS_FOR_CLINIC,
  {
    options: {
      refetchQueries: ['getAnotherUserAccess'],
      awaitRefetchQueries: true,
    },
    props: ({ mutate }: OptionProps<any>) => ({
      cancelSubscriptionsForClinic: (
        ids: string[],
        userId: string,
        userRole: string,
      ) =>
        mutate({
          variables: {
            ids,
            user: {
              userId: userId,
              role: userRole,
            },
          },
        })
          .then((_) => {
            toast(SUCCESSFULLY_CANCEL_SUBSCRIPTION_MSG, { type: 'info' });
          })
          .catch((error: Error) => {
            logError('FAILED CANCEL SUBSCRIPTION', error);
            toast(
              `${FAILED_CANCEL_SUBSCRIPTION_MSG}: ${messageFromError(error)}`,
              { type: 'error' },
            );
          }),
    }),
  },
);

export const makeFreeTrialSubscriptionForClinicMutation = graphql(
  SET_FREE_TRIAL_SUBSCRIPTION_FOR_CLINIC,
  {
    options: {
      refetchQueries: ['getAnotherUserAccess'],
      awaitRefetchQueries: true,
    },
    props: ({ mutate }: OptionProps<any>) => ({
      setFreeTrialClinicSubscription: (
        clinicId: string,
        isManualSubscribed: boolean,
        manualSubscribedTillDate?: Date,
      ) =>
        mutate({
          variables: {
            clinicId,
            isManualSubscribed,
            manualSubscribedTillDate,
          },
        })
          .then((_) => {
            toast(
              isManualSubscribed
                ? SUCCESSFULLY_SET_CLINIC_FREE_TRIAL_SUBSCRIPTION_PLAN_MSG
                : SUCCESSFULLY_STOP_CLINIC_FREE_TRIAL_SUBSCRIPTION_PLAN_MSG,
              {
                type: 'info',
              },
            );
          })
          .catch((error: Error) => {
            logError('FAILED CHANGE CLINIC FREE SUBSCRIPTION STATE', error);
            toast(
              `${FAILED_CHANGE_FREE_TRIAL_CLINIC_SUBSCRIPTION}: ${messageFromError(
                error,
              )}`,
              { type: 'error' },
            );
          }),
    }),
  },
);

export const makeSubscriptionForClinicMutation = graphql(
  MAKE_SUBSCRIPTION_FOR_CLINIC,
  {
    options: {
      refetchQueries: ['getAnotherUserAccess'],
      awaitRefetchQueries: true,
    },
    props: ({ mutate }: OptionProps<any>) => ({
      makeClinicSubscription: (
        clinicId: string,
        fee: Fee,
        admin: ClinicAdmin,
        email: string,
        name: string,
      ) =>
        mutate({
          variables: {
            clinicId,
            fee,
            admin,
            email,
            name,
          },
        })
          .then((_) => {
            toast(SUCCESSFULLY_CREATED_CLINIC_SUBSCRIPTION_PLAN_MSG, {
              type: 'info',
            });
          })
          .catch((error: Error) => {
            logError('FAILED CREATE SUBSCRIPTION', error);
            toast(
              `${FAILED_CREATE_SUBSCRIPTION_MSG}: ${messageFromError(error)}`,
              { type: 'error' },
            );
          }),
    }),
  },
);

export const getDoctorBySmartlinkQuery = <
  TProps extends { smartlinkTokenDoctor: string },
>() =>
  graphql(GET_DOCTOR_BY_SMARTLINK, {
    options: ({ smartlinkTokenDoctor }: TProps) => {
      return {
        fetchPolicy: 'network-only',
        variables: {
          token: smartlinkTokenDoctor,
        },
      };
    },

    props: ({ data }: OptionProps<TProps>) => {
      return {
        smartlinkDoctor: !data.loading ? data.getDoctorBySmartlink : null,
        isDoctorLinkLoading: data.loading,
      };
    },
  });

export const getClinicBySmartlinkQuery = <
  TProps extends { smartlinkTokenClinic: string },
>() =>
  graphql(GET_CLINIC_BY_SMARTLINK, {
    options: ({ smartlinkTokenClinic }: TProps) => {
      return {
        fetchPolicy: 'network-only',
        variables: {
          token: smartlinkTokenClinic,
        },
      };
    },
    props: ({ data }: OptionProps<TProps>) => {
      return {
        smartlinkClinic: !data.loading ? data.getClinicBySmartlink : null,
        isClinicLinkLoading: data.loading,
      };
    },
  });

export const getUserAccessQuery = <T>() =>
  graphql(USER_ACCESS, {
    options: {
      fetchPolicy: 'network-only',
    },
    props: ({ data }: OptionProps<T>) => {
      return {
        userAccess: data.getUserAccess as UserAccess,
        loadingUserHasSubscription: data.loading,
      };
    },
  });

export const medicinesProceduresQuery = graphql(MEDICINES_PROCEDURES_QUERY, {
  props: ({ data }) => ({
    medicines: pathOr([], ['medicines'], data),
    procedures: pathOr([], ['procedures'], data),
  }),
});

export const doctorsForClinicQuery = graphql(DOCTORS_FOR_CLINIC, {
  options: {
    variables: { isSigned: true, search: '', orderBy: 'active_DESC' },
    fetchPolicy: 'cache-and-network',
  },
  props: ({ data }) => ({
    doctors: pathOr([], ['current'], data).filter(
      (doctor: UserType) => doctor.active,
    ),
    refetchDoctorsQuery: data.refetch,
  }),
});

export const updatePatientByClinicMutation = <
  T extends { patient: PatientType },
>(
  onSuccessEvent?: (props: T) => void,
) =>
  graphql(UPDATE_PATIENT_BY_CLINIC_MUTATION, {
    options: { refetchQueries: ['patientsForClinicDoctor'] },
    props: ({ mutate, ownProps }: OptionProps<T>) => {
      const { patient } = ownProps;
      return {
        submitForm: (values: IFormEditPatientByClinicValues) => {
          const { medicines, procedures } = values;

          return mutate({
            variables: {
              patientId: patient.id,
              ...values,
              doctorId:
                values.doctor && values.doctor.value
                  ? values.doctor.value
                  : null,
              avatar: pathOr(undefined, ['avatar', 0], values),
              ...setProceduresInput(procedures),
              ...setMedicinesInput(medicines),
            },
          })
            .then(() => {
              toast(SUCCESSFULLY_EDIT_PATIENT(values), { type: 'info' });
              onSuccessEvent && onSuccessEvent(ownProps);
            })
            .catch((error: Error) => {
              logError('EDIT PATIENT BY CLINIC', error);
              toast(FAILURE_EDIT_PATIENT(patient, error), { type: 'error' });
            });
        },
      };
    },
  });
